import { useState, useEffect, useRef } from 'react';

const EditableTitle = ({ 
  value, 
  onSave, 
  placeholder = "Untitled Document",
  className = "",
  isDocument = true // true for documents, false for folders
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Update local value when prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select(); // Select all text
    }
  }, [isEditing]);

  // Handle click to enter edit mode
  const handleClick = () => {
    if (!isEditing && !isSaving) {
      setIsEditing(true);
      setEditValue(value);
    }
  };

  // Handle saving the title
  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    
    // Don't save if empty or unchanged
    if (!trimmedValue || trimmedValue === value) {
      setIsEditing(false);
      setEditValue(value); // Reset to original value
      return;
    }

    setIsSaving(true);
    
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save title:', error);
      // Reset to original value on error
      setEditValue(value);
      setIsEditing(false);
      // TODO: Show error message to user
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel editing
  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  // Handle key presses
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Handle blur (click outside)
  const handleBlur = () => {
    // Small delay to allow for save button clicks
    setTimeout(() => {
      if (isEditing && !isSaving) {
        handleSave();
      }
    }, 100);
  };

  // Show loading state while saving
  if (isSaving) {
    return (
      <div className={`flex items-center ${className}`}>
        <span className="text-sm font-medium text-gray-900">{editValue}</span>
        <div className="ml-2 w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show input when editing
  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`bg-white border border-blue-500 rounded px-2 py-1 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${className}`}
        placeholder={placeholder}
        maxLength={isDocument ? 100 : 50} // Different limits for docs vs folders
      />
    );
  }

  // Show clickable title when not editing
  return (
    <button
      onClick={handleClick}
      className={`text-left hover:bg-gray-100/60 rounded px-2 py-1 transition-colors duration-200 ${className}`}
      title="Click to edit"
    >
      <span className="text-sm font-medium text-gray-900">
        {value || placeholder}
      </span>
    </button>
  );
};

export default EditableTitle;